import { useState, useEffect, useCallback } from 'react'
import { Row, Col, Progress, Spin, Table, Typography, Card, Statistic, Empty } from 'antd'
import { WalletOutlined, ThunderboltOutlined, CalendarOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import {
  ProfileService,
  type CreditQuotaInfo,
  type CreditTransaction,
} from '../../services/profile/profileService'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const { Text } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg};
`

const QuotaCard = styled(Card)`
  .ant-card-body {
    padding: ${SPACING.lg};
  }
`

const QuotaHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.lg};
`

const QuotaIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${BORDER_RADIUS.lg};
  background: var(--color-primary-bg);
  color: var(--color-primary);
  font-size: ${TYPOGRAPHY.h3.fontSize};
`

const QuotaTitle = styled.div`
  flex: 1;
`

const QuotaTitleText = styled.h3`
  margin: 0;
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
`

const QuotaSubtitle = styled.p`
  margin: 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

const ProgressSection = styled.div`
  margin-bottom: ${SPACING.lg};
`

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${SPACING.xs};
`

const StatsRow = styled(Row)`
  margin-top: ${SPACING.md};
`

const TransactionsCard = styled(Card)`
  .ant-card-body {
    padding: ${SPACING.md};
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${SPACING.xxl};
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${SPACING.xl};
  color: var(--color-error);
`

function formatCredits(credits: number): string {
  if (Math.abs(credits) >= 1000000) {
    return `${(credits / 1000000).toFixed(2)}M`
  }
  if (Math.abs(credits) >= 1000) {
    return `${(credits / 1000).toFixed(2)}K`
  }
  return credits.toFixed(2)
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getOperationLabel(operation: string | null, transactionType?: string): string {
  // Handle allowance transactions specially
  if (transactionType === 'allowance' && operation === 'monthly_reset') {
    return 'Monthly Credit Refresh'
  }

  if (!operation) return 'Unknown'

  const labels: Record<string, string> = {
    generate_idea: 'Generate Idea',
    generate_needs: 'Generate Unmet Needs',
    generate_figma_prototype: 'Export UI Prompt',
    chat: 'AI Chat',
    generate_ratings: 'Generate Ratings',
    assign_phase: 'Assign Phase',
    generate_portrait: 'Generate Portrait',
    generate_storyboard: 'Generate Storyboard',
    generate_storyboard_descriptions: 'Storyboard Descriptions',
    monthly_reset: 'Monthly Credit Refresh',
  }

  return labels[operation] || operation.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const PAGE_SIZE = 20

export function CreditUsageSection() {
  const [quota, setQuota] = useState<CreditQuotaInfo | null>(null)
  const [transactions, setTransactions] = useState<CreditTransaction[]>([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadQuota = useCallback(async () => {
    try {
      const { data, error } = await ProfileService.getCreditQuota()
      if (error) {
        setError(error.message)
        return
      }
      if (data) {
        setQuota(data)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit quota')
    }
  }, [])

  const loadTransactions = useCallback(async (page: number = 1) => {
    setTableLoading(true)
    try {
      const offset = (page - 1) * PAGE_SIZE
      const { data, error } = await ProfileService.getCreditUsage({ limit: PAGE_SIZE, offset })
      if (error) {
        setError(error.message)
        return
      }
      if (data) {
        setTransactions(data.transactions)
        setTotal(data.total || 0)
        setCurrentPage(page)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions')
    } finally {
      setTableLoading(false)
    }
  }, [])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      await Promise.all([loadQuota(), loadTransactions()])
      setLoading(false)
    }
    void load()
  }, [loadQuota, loadTransactions])

  const handleTableChange = (page: number) => {
    void loadTransactions(page)
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    )
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>
  }

  const usagePercent =
    quota && !quota.isUnlimited && quota.creditLimit > 0
      ? Math.min(100, (quota.creditsUsed / quota.creditLimit) * 100)
      : 0

  const progressStatus =
    usagePercent >= 90 ? 'exception' : usagePercent >= 75 ? 'normal' : 'success'

  const columns = [
    {
      title: 'Operation',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: string | null, record: CreditTransaction) => {
        const isAllowance = record.transaction_type === 'allowance'
        const label = getOperationLabel(record.operation, record.transaction_type)
        return (
          <Text strong style={isAllowance ? { color: '#1890ff' } : undefined}>
            {isAllowance && <CalendarOutlined style={{ marginRight: 6 }} />}
            {label}
          </Text>
        )
      },
    },
    {
      title: 'Credits',
      dataIndex: 'credits_amount',
      key: 'credits_amount',
      width: 100,
      align: 'right' as const,
      render: (amount: number, record: CreditTransaction) => {
        const isAllowance = record.transaction_type === 'allowance'
        // Allowance is always positive (credits restored)
        // Usage is always negative (credits consumed)
        const color = amount > 0 || isAllowance ? '#52c41a' : 'var(--color-error)'
        const prefix = amount > 0 || isAllowance ? '+' : '-'
        return (
          <Text style={{ color, fontWeight: isAllowance ? 600 : 400 }}>
            {prefix}
            {formatCredits(Math.abs(amount))}
          </Text>
        )
      },
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date: string) => <Text type="secondary">{formatDateTime(date)}</Text>,
    },
  ]

  return (
    <Container>
      <QuotaCard>
        <QuotaHeader>
          <QuotaIcon>
            <WalletOutlined />
          </QuotaIcon>
          <QuotaTitle>
            <QuotaTitleText>Credit Balance</QuotaTitleText>
            <QuotaSubtitle>Your monthly AI credit usage</QuotaSubtitle>
          </QuotaTitle>
        </QuotaHeader>

        {quota?.isUnlimited ? (
          <StatsRow gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Statistic
                title="Credits Used"
                value={quota.creditsUsed}
                precision={2}
                prefix={<ThunderboltOutlined />}
                formatter={(value) => formatCredits(Number(value))}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Statistic title="Status" value="Unlimited" valueStyle={{ color: '#52c41a' }} />
            </Col>
          </StatsRow>
        ) : quota ? (
          <>
            <ProgressSection>
              <ProgressLabel>
                <Text>
                  {formatCredits(quota.creditsUsed)} / {formatCredits(quota.creditLimit)} credits
                </Text>
                <Text type="secondary">{usagePercent.toFixed(1)}%</Text>
              </ProgressLabel>
              <Progress
                percent={usagePercent}
                status={progressStatus}
                showInfo={false}
                strokeWidth={12}
              />
            </ProgressSection>

            <StatsRow gutter={[16, 16]}>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Used"
                  value={quota.creditsUsed}
                  precision={2}
                  valueStyle={{ fontSize: TYPOGRAPHY.h4.fontSize }}
                  formatter={(value) => formatCredits(Number(value))}
                />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Remaining"
                  value={quota.creditsRemaining}
                  precision={2}
                  valueStyle={{
                    fontSize: TYPOGRAPHY.h4.fontSize,
                    color:
                      quota.creditsRemaining <= 0
                        ? '#ff4d4f'
                        : quota.creditsRemaining < quota.creditLimit * 0.1
                          ? '#faad14'
                          : '#52c41a',
                  }}
                  formatter={(value) => formatCredits(Number(value))}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Period Started"
                  value={formatDate(quota.periodStart)}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ fontSize: TYPOGRAPHY.body.fontSize }}
                />
              </Col>
            </StatsRow>
          </>
        ) : null}
      </QuotaCard>

      <TransactionsCard title="Transaction History">
        {transactions.length > 0 ? (
          <Table
            dataSource={transactions}
            columns={columns}
            rowKey="id"
            size="small"
            loading={tableLoading}
            pagination={{
              current: currentPage,
              pageSize: PAGE_SIZE,
              total: total,
              showSizeChanger: false,
              showTotal: (t) => `${t} transactions`,
              onChange: handleTableChange,
            }}
          />
        ) : (
          <Empty description="No transactions yet" />
        )}
      </TransactionsCard>
    </Container>
  )
}
